import React, {useCallback, useState} from 'react';
import {Grid, Typography, useMediaQuery, useTheme} from '@mui/material';
import ParticipantAvatar, {
  Participant
} from '@/components/participant/participant-avatar';
import {fetchOtherUserData} from '@/rest/user/fetch-other-user-data';
import AllAvatatarsDialog from '@/components/ui/dialogs/all-avatars-dialog';
import {useTranslation} from 'react-i18next';
import UserOverview, {UserInOverview} from '@/components/user/overview';
interface ParticipantsListProps {
  participants: Participant[];
  groupView?: boolean;
  participantsLimit?: number | null;
  onUserOverviewOpenChange: (isOpen: boolean) => void;
}

const ParticipantsList: React.FC<ParticipantsListProps> = ({
  participants,
  groupView = false,
  participantsLimit = null,
  onUserOverviewOpenChange
}) => {
  const {t} = useTranslation();

  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  const [userOverviewDialogOpen, setUserOverviewDialogOpen] = useState(false);
  const [selectedUserToOverview, setSelectedUserToOverview] =
    useState<UserInOverview | null>(null);
  const [allAvatarsDialogOpen, setAllAvatarsDialogOpen] = useState(false);
  let visibleParticipants;
  if (isLg) {
    visibleParticipants = 9;
  } else if (isMd) {
    visibleParticipants = 6;
  } else if (isSm) {
    visibleParticipants = 5;
  } else {
    visibleParticipants = 3;
  }

  const getParticipantsCountText = () => {
    const count = participants.length;
    const category = groupView ? 'members' : 'participants';
    let key;

    if (
      participantsLimit !== null &&
      (participantsLimit < 5 || (participantsLimit >= 5 && count))
    ) {
      key = 'plural_with_limit';
    } else if (count === 0) {
      key = 'none';
    } else if (count === 1) {
      key = 'singular';
    } else {
      key = 'plural';
    }

    const translationKey = `${category}_screen_${key}`;

    return t(translationKey, {count, limit: participantsLimit});
  };

  const hiddenParticipants = participants.length - visibleParticipants;

  const handleUserOverviewClick = useCallback(
    (userId: string) => {
      fetchOtherUserData(userId)
        .then((fetchedUser) => {
          setSelectedUserToOverview(fetchedUser);
          setUserOverviewDialogOpen(true);
          onUserOverviewOpenChange(true);
        })
        .catch((error) => {
          console.error('Failed to fetch user', error);
        });
    },
    [onUserOverviewOpenChange]
  );

  const handleUserOverviewClose = useCallback(() => {
    setSelectedUserToOverview(null);
    setUserOverviewDialogOpen(false);
    onUserOverviewOpenChange(false);
  }, [onUserOverviewOpenChange]);

  const handleAllAvatarsDialogOpen = useCallback(() => {
    setAllAvatarsDialogOpen(true);
  }, []);

  const handleAllAvatarsDialogClose = useCallback(() => {
    setAllAvatarsDialogOpen(false);
  }, []);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography
            variant="h5"
            component="div"
            sx={{
              marginY: 2,
              textAlign: {
                xs: 'center',
                sm: 'left'
              }
            }}
          >
            {getParticipantsCountText()}
          </Typography>

          <Grid container spacing={2} sx={{marginLeft: 1}}>
            {participants
              .slice(0, visibleParticipants)
              .map((participant, index) => (
                <Grid item key={participant._id}>
                  <ParticipantAvatar
                    participant={participant}
                    index={index}
                    onClick={() => handleUserOverviewClick(participant._id)}
                  />
                </Grid>
              ))}
            {hiddenParticipants > 0 && (
              <Grid item>
                <ParticipantAvatar
                  participant={{
                    additionalInfo: `+${hiddenParticipants}`,
                    _id: 'hidden',
                    name: undefined,
                    birthday: undefined,
                    color: undefined,
                    gender: undefined,
                    picture: undefined,
                    updatedAt: undefined
                  }}
                  index={visibleParticipants}
                  onClick={handleAllAvatarsDialogOpen}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      {selectedUserToOverview && (
        <UserOverview
          userData={selectedUserToOverview}
          userViewDialogOpen={userOverviewDialogOpen}
          onClose={handleUserOverviewClose}
        />
      )}
      <AllAvatatarsDialog
        open={allAvatarsDialogOpen}
        onClose={handleAllAvatarsDialogClose}
        participants={participants}
        onAvatarClick={handleUserOverviewClick}
      />
    </>
  );
};

export default ParticipantsList;
